import type { WaveWeek } from './model'

export interface SkuWaveLine {
  id: string
  sourceRow: number
  sku: string
  description: string
  type: 'Single SKU' | 'Parent' | 'Child' | 'Replen'
  vendor: string
  stores: number
  cp: number
  ip: number
  cube: number
  cost: number
  retail: number
  childPurchaseUnits: number | null
  purchaseQty: number
  moq: number
  pogSet: number
  pogMax: number
  totalQty: number
  totalInnerPacks: number | null
  avgStoreUnits: number | null
  workbookNote: string | null
}

export interface SkuWaveFixture {
  id: string
  description: string
  setDate: string
  exitDate: string
  pogDescription: string
  location: string
  status: string | null
  stores: number
  dcCount: number
  sellableCount: number
  shippingCount: number
  piabCount: number
  cicoCount: number
  repackCount: number
  repackShipQty: number
  lines: SkuWaveLine[]
}

export interface SkuWaveAllocation {
  percent: number | null
  qty: number
  avgStore: number | null
  cubicFtPerDc: number | null
}

export interface SkuWaveColumn extends WaveWeek {
  index: number
  overallPercent: number
  outside: boolean
}

export interface SkuWaveRow {
  id: string
  line: SkuWaveLine | null
  waves: Record<number, SkuWaveAllocation>
  totalQty: number
  allocatedQty: number
  totalPercent: number | null
  adjusted: boolean
  outsideWeeks: number[]
  needsReview: boolean
}

export interface SkuWaveComment {
  id: string
  content: string
  authorId: string
  authorName: string
  createdAt: string
}

export type SkuWaveDrafts = Record<string, number[]>
export const ALLOCATION_TOLERANCE = 1e-7
export const isBalanced = (value: number | null) =>
  value !== null && Math.abs(value - 1) < ALLOCATION_TOLERANCE

const divide = (value: number, denominator: number): number | null =>
  denominator > 0 ? value / denominator : null

function sumAvailable(values: (number | null)[]): number | null {
  return values.some(value => value === null)
    ? null
    : values.reduce<number>((sum, value) => sum + (value ?? 0), 0)
}

/** Keep the allocated total while assigning whole units to the largest remainders. */
function wholeWaveQuantities(
  totalQty: number,
  percentages: number[]
): number[] {
  const theoretical = percentages.map(percent => {
    const qty = totalQty * percent
    const nearest = Math.round(qty)
    return Math.abs(qty - nearest) < 1e-9 ? nearest : qty
  })
  const quantities = theoretical.map(Math.floor)
  const remaining =
    Math.round(theoretical.reduce((sum, qty) => sum + qty, 0)) -
    quantities.reduce((sum, qty) => sum + qty, 0)
  const ranked = theoretical
    .map((qty, index) => ({ index, remainder: qty - quantities[index] }))
    .filter(item => item.remainder > 0)
    .sort((a, b) => {
      const difference = b.remainder - a.remainder
      return Math.abs(difference) < 1e-9 ? a.index - b.index : difference
    })
  ranked.slice(0, remaining).forEach(({ index }) => quantities[index]++)
  return quantities
}

export function calculateSkuPlanning(
  fixture: SkuWaveFixture,
  weeks: WaveWeek[],
  overallPercentages: number[],
  drafts: SkuWaveDrafts = {}
) {
  const rows: SkuWaveRow[] = fixture.lines.map(line => {
    const requestedPercentages = drafts[line.id] ?? overallPercentages
    const percentages = weeks.map((week, index) =>
      week.event === 'NO WAVE' ? 0 : (requestedPercentages[index] ?? 0)
    )
    const quantities = wholeWaveQuantities(line.totalQty, percentages)
    const waves: Record<number, SkuWaveAllocation> = {}
    const outsideWeeks: number[] = []
    let totalPercent = 0
    let allocatedQty = 0
    weeks.forEach((week, index) => {
      const percent = percentages[index]
      const qty = quantities[index]
      totalPercent += percent
      allocatedQty += qty
      if (percent > 0 && !(overallPercentages[index] > 0))
        outsideWeeks.push(week.week)
      waves[week.week] = {
        percent,
        qty,
        avgStore: divide(qty, line.stores),
        cubicFtPerDc:
          line.cp > 0
            ? divide((qty / line.cp) * line.cube, fixture.dcCount)
            : null,
      }
    })
    return {
      id: line.id,
      line,
      waves,
      totalQty: line.totalQty,
      allocatedQty,
      totalPercent,
      adjusted: !!drafts[line.id],
      outsideWeeks,
      needsReview: !isBalanced(totalPercent) || outsideWeeks.length > 0,
    }
  })
  const visibleWeeks: SkuWaveColumn[] = weeks.flatMap((week, index) => {
    if (week.event === 'NO WAVE') return []
    const overallPercent = overallPercentages[index] ?? 0
    if (
      overallPercent <= 0 &&
      !rows.some(row => (row.waves[week.week].percent ?? 0) > 0)
    )
      return []
    return [{ ...week, index, overallPercent, outside: overallPercent <= 0 }]
  })
  return { rows, visibleWeeks, summary: aggregateSkuRows(rows, visibleWeeks) }
}

/** Match the workbook's distinct populations: units exclude Parent; cube excludes Child. */
export function aggregateSkuRows(
  rows: SkuWaveRow[],
  weeks: Pick<WaveWeek, 'week'>[]
): SkuWaveRow {
  const unitRows = rows.filter(row => row.line?.type !== 'Parent')
  const volumeRows = rows.filter(row => row.line?.type !== 'Child')
  const totalQty = unitRows.reduce((sum, row) => sum + row.totalQty, 0)
  const allocatedQty = unitRows.reduce((sum, row) => sum + row.allocatedQty, 0)
  const waves: Record<number, SkuWaveAllocation> = {}
  weeks.forEach(({ week }) => {
    const qty = unitRows.reduce((sum, row) => sum + row.waves[week].qty, 0)
    waves[week] = {
      percent: divide(qty, totalQty),
      qty,
      avgStore: sumAvailable(unitRows.map(row => row.waves[week].avgStore)),
      cubicFtPerDc: sumAvailable(
        volumeRows.map(row => row.waves[week].cubicFtPerDc)
      ),
    }
  })
  return {
    id: 'filtered-totals',
    line: null,
    waves,
    totalQty,
    allocatedQty,
    totalPercent: divide(allocatedQty, totalQty),
    adjusted: false,
    outsideWeeks: [],
    needsReview: rows.some(row => row.needsReview),
  }
}
